import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { RealCostRepository } from "../../../domain/repositories/real-cost.repository";
import { RealCostResponse, RealCostData, RealCostMultiplier, RealCostRecord, ResourceCostOverride, BillingEvent } from "../../../domain/models/real-cost.model";
import { environment } from "../../../../environments/environment";

@Injectable()
export class RealCostRepositoryImpl extends RealCostRepository {
  private readonly endpoint = environment.apiUrl;

  constructor(private http: HttpClient) {
    super();
  }

  override loadRealCost(contractCode: string, billingEventId?: number | null): Observable<RealCostData> {
    return this.http
      .get<GetRealCostResponse>(`${this.endpoint}/rc/realCost`, {
          params: {
              contractCode,
              ...(billingEventId != null
                  ? { billingEventId }
                  : {})
          },
      })
      .pipe(
        map((response: GetRealCostResponse) => {
          return {
            contractId: response.realCost.contractId,
            contractCode: response.realCost.contractCode,
            invoicePreviewCode: response.realCost.invoicePreviewCode,
            invoicePreviewCreatedAt: response.realCost.invoicePreviewCreatedAt,
            billingEvents: response.realCost.billingEvents,
            straight40: response.realCost.straight40,
            multipliers: response.realCost.multipliers,
            records: response.realCost.records
          } as RealCostData;
        })
      );
  }

  override saveMultipliers(contractId: number, multipliers: RealCostMultiplier[] | null, straight40?: boolean | null): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/rc/multipliers`, {
      contractId,
      multipliers,
      straight40
    });
  }

  override loadCostOverrides(contractId: number): Observable<ResourceCostOverride[]> {
    return this.http
      .get<GetCostOverridesResponse>(`${this.endpoint}/rc/costOverrides`, {
        params: { contractId: contractId.toString() },
      })
      .pipe(map((r) => r.overrides ?? []));
  }

  override saveCostOverrides(contractId: number, overrides: ResourceCostOverride[]): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/rc/costOverrides`, { contractId, overrides });
  }

  override checkPageAccess(ruleCode: string): Observable<boolean> {
    return this.http
      .get<{ hasAccess: boolean }>(`${this.endpoint}/rc/checkAccess`, {
        params: { ruleCode },
      })
      .pipe(map(r => r.hasAccess));
  }
}

interface GetRealCostResponse {
  realCost: {
    contractId: number;
    contractCode: string;
    invoicePreviewCode?: string;
    invoicePreviewCreatedAt?: string;
    billingEvents: BillingEvent[];
    straight40?: boolean | null;
    multipliers: RealCostMultiplier[];
    records: RealCostRecord[];
  };
}

interface GetCostOverridesResponse {
  overrides: ResourceCostOverride[];
}
